import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
//zustand
import { useCartStore } from 'stores/cartStore';
import { useUserStore } from 'stores/userStore';
import { useCompanyStore } from 'stores/companyStore';
import { toast } from 'react-toastify';
// images
import preview from 'assets/img/preview.jpg';
import giftImageCard from 'assets/img/giftCard.jpg';
// icons
import { BiCheck, BiMinus } from 'react-icons/bi';
import { BsPlus, BsArrowsFullscreen } from 'react-icons/bs';
import { FiTrash2 } from 'react-icons/fi';
//api
import { addItemCart, decreaseQuantityItemCart, removeItemCart, deleteGiftCardCart } from 'api/ecommerce';
import { getDetailProduct } from 'api/products';
import {
  buildAddItemCartPayload,
  buildCartLineMutationPayload,
  normalizeAddItemCartResponse,
  productNeedsConditionChoice,
  isBookProduct,
  bookConditionFlagsMissing,
  hydrateBookConditionFlags,
} from 'utils/bookCondition';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
//helpers
import { detailTypeProductRedirect } from 'helpers';
import { formatCurrency } from 'utils/currencyFormatter';
import { getCartLinePriceDisplay } from 'utils/priceDiscount';
//components
import Image from 'components/image';
import WatchModalUser from "components/userzone/modal";
import BookConditionChoiceModal from 'components/cart/BookConditionChoiceModal';
import './cart.scss';

const Items = (props) => {
  const { assignedLibrary} = useAssignedLibrary();

  const { items, hasGiftCard, giftCard, removeItem, increaseQuantity, decreaseQuantity, removeGiftCard, addItem } = useCartStore();
  const { shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping } = useCompanyStore();
  const { shippingAddress, isLogged, isConcourseUser, info_concourse } = useUserStore();

  const [modalShow, setModalShow] = useState(false);
  const [item, setItem] = useState('');
  const [conditionModalProduct, setConditionModalProduct] = useState(null);
  const [openingConditionModal, setOpeningConditionModal] = useState(false);
  const [isChangingCondition, setIsChangingCondition] = useState(false);
  const [metadataById, setMetadataById] = useState({});
  const requestedMetaIdsRef = useRef(new Set());

  const { t } = useTranslation(['cart', 'detalle']);

  const concourseDiscount = isConcourseUser ? Number(info_concourse?.discount) || 15 : null;

  const labelBookCondition = (cond) => {
    if (!cond) return '';
    if (cond === 'new') return t('bookConditionNew', { ns: 'detalle' });
    if (cond === 'used') return t('bookConditionUsed', { ns: 'detalle' });
    if (cond === 'newbalance') return t('bookConditionLikeNew', { ns: 'detalle' });
    return cond;
  };

  const replaceBookCondition = useCallback(
    async (cartLine, newCondition) => {
      if (!cartLine) return false;
      if (newCondition === cartLine.book_condition) return true;
      const province = shippingAddress?.province?.label || shippingAddress?.province || '';
      const productPayload = { ...cartLine, book_condition: newCondition };
      let addedLine;

      setIsChangingCondition(true);
      try {
        if (isLogged) {
          const { status: stRemove } = await removeItemCart(buildCartLineMutationPayload(cartLine));
          if (stRemove !== 200) {
            toast.error(t('bookConditionChangeError'), { theme: 'colored' });
            return false;
          }
          const { status: stAdd, data } = await addItemCart(
            buildAddItemCartPayload(cartLine.id, cartLine.quantity, productPayload, newCondition)
          );
          addedLine = normalizeAddItemCartResponse(data);
          if (stAdd !== 201) {
            toast.error(t('bookConditionChangeError'), { theme: 'colored' });
            return false;
          }
        }
        removeItem(cartLine, shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping, isConcourseUser);
        const result = addItem({
          product: addedLine?.unit_price != null
            ? {
                ...productPayload,
                unit_price: addedLine.unit_price,
                price: addedLine.unit_price,
                book_condition: addedLine.book_condition ?? newCondition,
              }
            : productPayload,
          province,
          shippingCost: shipping_cost,
          shippingCostIslands: shipping_cost_islands,
          minPurchaseForFreeShipping: min_purchase_for_free_shipping,
          quantity: cartLine.quantity,
          setQuantity: true,
          isConcourseUser,
          concourseDiscount,
          bookCondition: newCondition,
        });
        if (!result?.success) {
          const errorMessage = result?.errorCode
            ? t(`cart:${result.errorCode}`, result.params || {})
            : t('bookConditionChangeError');
          toast.error(errorMessage, { theme: 'colored' });
          return false;
        }
        return true;
      } catch (e) {
        console.error(e);
        toast.error(t('anUnexpectedErrorOccurred'), { theme: 'colored' });
        return false;
      } finally {
        setIsChangingCondition(false);
      }
    },
    [
      addItem,
      concourseDiscount,
      isConcourseUser,
      isLogged,
      min_purchase_for_free_shipping,
      removeItem,
      shipping_cost,
      shipping_cost_islands,
      shippingAddress,
      t,
    ]
  );

  const handleOpenConditionModal = async (cartItem) => {
    if (props?.isResume) return;
    setOpeningConditionModal(true);
    try {
      let product = cartItem;
      if (bookConditionFlagsMissing(cartItem)) {
        product = await hydrateBookConditionFlags({ ...cartItem });
      }
      if (!productNeedsConditionChoice(product)) {
        toast.info(t('bookConditionChangeUnavailable'), { theme: 'colored' });
        return;
      }
      setConditionModalProduct(product);
    } catch (e) {
      console.error(e);
      toast.error(t('anUnexpectedErrorOccurred'), { theme: 'colored' });
    } finally {
      setOpeningConditionModal(false);
    }
  };

  const handleCloseConditionModal = () => setConditionModalProduct(null);

  const handleConfirmConditionChange = async (newCondition) => {
    if (!conditionModalProduct) return;
    const ok = await replaceBookCondition(conditionModalProduct, newCondition);
    if (ok) setConditionModalProduct(null);
  };

  const increaseQuantityItem = async (cartLine) => {
    const province = shippingAddress?.province?.label || shippingAddress?.province || '';
    
    if (isLogged) {
      const payload = buildAddItemCartPayload(cartLine.id, 1, cartLine, cartLine.book_condition);
      const { status, data } = await addItemCart(payload);
      if (status === 201) {
        const addedLine = normalizeAddItemCartResponse(data);
        const lineWithUnitPrice = addedLine?.unit_price != null
          ? { ...cartLine, unit_price: addedLine.unit_price, price: addedLine.unit_price }
          : cartLine;
        const result = increaseQuantity(lineWithUnitPrice, shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping, province, isConcourseUser);
        if (!result?.success) {
          const errorMessage = result?.errorCode 
          ? t(`cart:${result.errorCode}`, result.params || {})
          : t('cart:errorIncreaseQuantity');
        toast.error(errorMessage, { theme: 'colored' });
        }
      } else {
        console.log('error');
      }
    } else {
      const result = increaseQuantity(cartLine, shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping, province, isConcourseUser);
      if (!result?.success) {
        const errorMessage = result?.errorCode 
          ? t(`cart:${result.errorCode}`, result.params || {})
          : t('cart:errorIncreaseQuantity');
        toast.error(errorMessage, { theme: 'colored' });
      }
    }
  };

  const decreaseQuantityItem = async (cartLine) => {
    const province = shippingAddress?.province?.label || shippingAddress?.province || '';
    if (isLogged) {
      const payload = { ...buildCartLineMutationPayload(cartLine), quantity: 1 };
      const { status } = await decreaseQuantityItemCart(payload);
      if (status === 200) {
        decreaseQuantity(cartLine, shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping, province, isConcourseUser);
      } else {
        console.log('error');
      }
    } else {
      decreaseQuantity(cartLine, shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping, province, isConcourseUser);
    }
  };

  const removeProductCart = async (item) => {
    if (isLogged) {
      const payload = buildCartLineMutationPayload(item);
      const { status } = await removeItemCart(payload);
      if (status === 200) {
        removeItem(item, shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping, isConcourseUser);
      } else {
        console.log('error');
      }
    } else {
      removeItem(item, shipping_cost, shipping_cost_islands, min_purchase_for_free_shipping, isConcourseUser);
    }
  };

  const removeGiftCardCart = async () => {
    removeGiftCard();
    if (isLogged) {
      const payload = { item: giftCard };
      const { status } = await deleteGiftCardCart(payload);
      if (status === 200) {
      } else {
        console.log('error');
      }
    } else {
      removeGiftCard();
    }
  };

  const handleFullScreen = (img) => {
    setItem(img);
    setModalShow(true);
  };

  const getProductMeta = (cartItem) => {
    const author =
      (cartItem.authors?.length > 0 && (cartItem.authors[0].nombre_autor || cartItem.authors[0].name)) ||
      (cartItem.product_detail?.authors?.length > 0 &&
        (cartItem.product_detail.authors[0].nombre_autor || cartItem.product_detail.authors[0].name)) ||
      cartItem.nombre_autor ||
      cartItem.author ||
      metadataById[cartItem.id]?.author ||
      '';
    const editorial =
      cartItem.editorial?.nombre_fiscal ||
      cartItem.editorial?.label ||
      cartItem.product_detail?.editorial?.nombre_fiscal ||
      cartItem.product_detail?.editorial?.label ||
      cartItem.editorial ||
      cartItem.nombre_editorial ||
      cartItem.publisher ||
      metadataById[cartItem.id]?.editorial ||
      '';
    return { author, editorial };
  };

  const getProductMetaLine = (cartItem) => {
    const { author, editorial } = getProductMeta(cartItem);
    if (author && editorial) return `${author} · ${editorial}`;
    return author || editorial || '';
  };

  useEffect(() => {
    let isMounted = true;

    const getDetailMeta = (productDetail) => {
      const author =
        (productDetail?.authors?.length > 0 &&
          (productDetail.authors[0].nombre_autor || productDetail.authors[0].name)) ||
        productDetail?.nombre_autor ||
        '';
      const editorial =
        productDetail?.editorial?.nombre_fiscal ||
        productDetail?.editorial?.label ||
        productDetail?.nombre_editorial ||
        '';
      return { author, editorial };
    };

    const idsToFetch = items
      .filter((cartItem) => {
        if (!cartItem?.id) return false;
        const hasAuthor =
          (cartItem.authors?.length > 0 && (cartItem.authors[0].nombre_autor || cartItem.authors[0].name)) ||
          (cartItem.product_detail?.authors?.length > 0 &&
            (cartItem.product_detail.authors[0].nombre_autor || cartItem.product_detail.authors[0].name)) ||
          cartItem.nombre_autor ||
          cartItem.author;
        const hasEditorial =
          cartItem.editorial?.nombre_fiscal ||
          cartItem.editorial?.label ||
          cartItem.product_detail?.editorial?.nombre_fiscal ||
          cartItem.product_detail?.editorial?.label ||
          cartItem.editorial ||
          cartItem.nombre_editorial ||
          cartItem.publisher;

        // Si falta cualquiera de los dos, pedimos detalle para completar metadata
        if (hasAuthor && hasEditorial) return false;
        if (requestedMetaIdsRef.current.has(cartItem.id)) return false;
        return true;
      })
      .map((cartItem) => cartItem.id);

    if (idsToFetch.length === 0) {
      return () => {
        isMounted = false;
      };
    }

    idsToFetch.forEach((id) => requestedMetaIdsRef.current.add(id));

    const fetchMissingMeta = async () => {
      const fetchedEntries = await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            const { status, data } = await getDetailProduct(id);
            if (status === 200 && data) {
              return [id, getDetailMeta(data)];
            }
          } catch (error) {
            console.error('Error getting product detail metadata', error);
          }
          return [id, { author: '', editorial: '' }];
        })
      );

      if (!isMounted) return;

      setMetadataById((prev) => {
        const next = { ...prev };
        fetchedEntries.forEach(([id, meta]) => {
          next[id] = meta;
        });
        return next;
      });
    };

    fetchMissingMeta();

    return () => {
      isMounted = false;
    };
  }, [items]);

  const renderUnitPrice = (cartItem) => {
    const { originalPrice, finalPrice, appliedDiscountPercents, hasDiscount } = getCartLinePriceDisplay(cartItem, {
      isConcourseUser,
      concoursePercent: concourseDiscount,
    });

    if (!hasDiscount) {
      return (
        <span className="cart-price-breakdown">
          <strong>{formatCurrency(finalPrice)}</strong>
        </span>
      );
    }

    return (
      <span className="cart-price-breakdown">
        <span className="text-decoration-line-through text-muted">{formatCurrency(originalPrice)}</span>
        <strong>{formatCurrency(finalPrice)}</strong>
        {appliedDiscountPercents.map((pct, index) => (
          <span key={`${pct}-${index}`} className="text-success">{`(-${pct}%)`}</span>
        ))}
      </span>
    );
  };

  if (props?.isResume) {
    return (
      <Row>
        <Col md={12}>
          {items?.map((cartItem, i) => (
            <div key={`${cartItem.id}-${cartItem.book_condition ?? i}`} className="cart-item cart-item--legacy">
              <Col sm={9} md={9} className="cart-item__details position-relative">
                <Link
                  className="cart-table__image-link cursor-pointer py-0"
                  to={detailTypeProductRedirect(cartItem, assignedLibrary)}
                >
                  <Image key={cartItem.id} src={cartItem.image} fallbackSrc={preview} height="100" />
                </Link>
                <Col sm={12} md={12}>
                  <Link
                    to={detailTypeProductRedirect(cartItem, assignedLibrary)}
                    className="cart-item__link-title"
                  >
                    <h2 title={cartItem?.title || cartItem?.titulo}>{cartItem?.title || cartItem?.titulo}</h2>
                  </Link>
                  {getProductMetaLine(cartItem) && (
                    <p className="container-authors__paragrap mr-5">{getProductMetaLine(cartItem)}</p>
                  )}
                </Col>
              </Col>
              <Col sm={3} md={3} className="cart-item__resume position-relative">
                <div className="infoQuantyItem">
                  <div className="dataItem">
                    <div className="fieldData">{`${t('quantity')}:`}</div>
                    <div className="valueData">{cartItem.quantity}</div>
                  </div>
                </div>
                <div className="infoItem">
                  <div className="dataItem">
                    <div className="fieldData">{`${t('price')}:`}</div>
                    <div className="valueData">{renderUnitPrice(cartItem)}</div>
                  </div>
                  <div className="dataItem">
                    <div className="fieldData">{`${t('total')}:`}</div>
                    <div className="valueData">
                      {formatCurrency(Number(cartItem.quantity) * (Number(cartItem.unit_price ?? cartItem.price) || 0))}
                    </div>
                  </div>
                </div>
              </Col>
            </div>
          ))}
        </Col>
      </Row>
    );
  }

  return (
    <>
      <div className="cart-table-card">
        <div className="cart-table">
          <div className="cart-table__head">
            <span className="cart-table__head-cell cart-table__head-cell--product">{t('productColumn')}</span>
            <span className="cart-table__head-cell cart-table__head-cell--qty">{t('quantityColumn')}</span>
            <span className="cart-table__head-cell cart-table__head-cell--price">{t('priceColumn')}</span>
            <span className="cart-table__head-cell cart-table__head-cell--action" aria-hidden="true" />
          </div>
          {items?.map((cartItem, i) => (
            <div key={`${cartItem.id}-${cartItem.book_condition ?? i}`} className="cart-table__row">
              <div className="cart-table__product">
                <div className="cart-table__image-wrap">
                  <Link
                    to={detailTypeProductRedirect(cartItem, assignedLibrary)}
                    className="cart-table__image-link"
                  >
                    <Image src={cartItem.image} fallbackSrc={preview} height="100" />
                  </Link>
                  {cartItem.image && (
                    <button
                      type="button"
                      className="cart-table__expand-btn"
                      aria-label={t('viewImage')}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFullScreen(cartItem.image);
                      }}
                    >
                      <BsArrowsFullscreen className="cart-table__expand-icon" aria-hidden="true" />
                    </button>
                  )}
                </div>
                <div className="cart-table__info">
                  <Link to={detailTypeProductRedirect(cartItem, assignedLibrary)} className="cart-item__link-title">
                    <h2 className="cart-table__title" title={cartItem?.title || cartItem?.titulo}>
                      {cartItem?.title || cartItem?.titulo}
                    </h2>
                  </Link>
                  {getProductMetaLine(cartItem) && <p className="cart-table__meta">{getProductMetaLine(cartItem)}</p>}
                  {cartItem.book_condition && productNeedsConditionChoice(cartItem) && (
                    <div className="cart-item__book-condition-wrap">
                      <div className="cart-item__book-condition-badge">
                        <span className="cart-item__book-condition-label">
                          {t('bookConditionChosen', { label: labelBookCondition(cartItem.book_condition) })}
                        </span>
                        {isBookProduct(cartItem) && (
                          <button
                            type="button"
                            className="cart-item__book-condition-change"
                            onClick={() => handleOpenConditionModal(cartItem)}
                            disabled={openingConditionModal || isChangingCondition}
                          >
                            {t('changeBookCondition')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <span className="cart-table__badge">
                    <BiCheck /> {t('available')}
                  </span>
                </div>
              </div>
              <div className="cart-table__qty-col">
                <div className="cart-table__qty">
                  <Link
                    to={`/${assignedLibrary}/carrito`}
                    onClick={() => decreaseQuantityItem(cartItem)}
                    aria-label={t('removeunitproduct')}
                  >
                    <BiMinus />
                  </Link>
                  <span className="cart-table__qty-value">{cartItem.quantity}</span>
                  <Link
                    to={`/${assignedLibrary}/carrito`}
                    onClick={() => increaseQuantityItem(cartItem)}
                    aria-label={t('addproduct')}
                  >
                    <BsPlus />
                  </Link>
                </div>
              </div>
              <div className="cart-table__price-col">
                <div className="cart-table__price">{renderUnitPrice(cartItem)}</div>
              </div>
              <div className="cart-table__action-col">
                <button
                  type="button"
                  className="cart-table__remove"
                  aria-label={t('removeitem')}
                  onClick={() => removeProductCart(cartItem)}
                >
                  <FiTrash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {hasGiftCard && (
        <div className="cart-table-card mt-3">
          <div className="cart-item cart-item--legacy">
            <div className="cart-item__details">
              <Image src={giftImageCard} className="style-giftcard" />
              <div className="d-flex flex-column">
                <h2>{t('giftcard')}</h2>
                <p className="mr-5 mt-5 style-paragraph">{t('couponname')}</p>
                <h3 className="mt-1">{giftCard?.title}</h3>
              </div>
            </div>
            <div className="cart-item__price">
              <div className="cart-item__price-value">
                <strong>{formatCurrency(giftCard?.discount_amount || 0)}</strong>
              </div>
              <p className="mr-5 mt-5 style-paragraph">{t('amountPrice')}</p>
              <Button className="white-btn p-1 style-button-delete" onClick={() => removeGiftCardCart()}>
                {t('remove').toUpperCase()}
              </Button>
            </div>
          </div>
        </div>
      )}

      <WatchModalUser item={item} show={modalShow} setShow={setModalShow} />
      <BookConditionChoiceModal
        show={!!conditionModalProduct}
        product={conditionModalProduct}
        onHide={handleCloseConditionModal}
        onConfirm={handleConfirmConditionChange}
        confirmLabel={t('confirmBookConditionUpdate')}
        confirmDisabled={isChangingCondition}
      />
    </>
  );
};

export default Items;
